/**
 * The sizes a frame pinned to the desktop may take, one rule per frame type
 * (plan 12 §8), read by the size row of the widget's ⋯, by pinning and by
 * every resize. A glance face, and a live frame made to show small (a
 * canvas widget, `WIDGET_FRAME_TYPES`), takes all three widget sizes; any
 * other live frame is too dense for a small card and takes medium or large.
 * A frame people work in (a Doc, Memory, a page, a file) is
 * sized freely by the hand, from its own smallest widget size up to its
 * display.
 */
import type { FrameType } from "@/api/spaces";
import { WIDGET_FRAME_TYPES } from "@/lib/frame-defaults";
import {
  WIDGET_SIZE_ORDER,
  WIDGET_SIZES,
  widgetFaceFor,
  type WidgetSize,
} from "./widget-face";

export type WidgetSizing =
  | {
      /** The widget sizes it snaps to, smallest first. */
      sizes: readonly WidgetSize[];
      /** The size a new pin starts at. */
      initial: WidgetSize;
    }
  | {
      /** Any size the hand drags, from `min` up to its display. */
      sizes: "free";
      initial: WidgetSize;
      /** The smallest widget size it shrinks to. */
      min: WidgetSize;
    };

/** A pinned widget's size: one of the widget sizes, or free (its rect's own). */
export type PinnedWidgetSize = WidgetSize | "free";

/** The smallest any widget gets while the hand drags it: a small widget. */
export const FREE_WIDGET_MIN = WIDGET_SIZES.small;

const sized = (
  sizes: readonly WidgetSize[],
  initial: WidgetSize,
): WidgetSizing => ({ sizes, initial });

/** Sized by the hand, never below `min`; it starts at `initial`. */
const free = (min: WidgetSize, initial: WidgetSize = min): WidgetSizing => ({
  sizes: "free",
  initial,
  min,
});
/** A face or a live frame made to show small. */
const ANY_SIZE = sized(WIDGET_SIZE_ORDER, "medium");
/** A live frame too dense for a small card. */
const ROOMY = sized(["medium", "large"], "medium");

const SIZINGS: Partial<Record<FrameType, WidgetSizing>> = {
  doc: free("large"),
  note: free("large"),
  markdown: free("large"),
  terminal: free("small", "large"),
  memory: free("large"),
  html: free("medium"),
  file: free("medium"),
  taskboard: sized(WIDGET_SIZE_ORDER, "large"),
  weather: sized(WIDGET_SIZE_ORDER, "small"),
  music: sized(WIDGET_SIZE_ORDER, "small"),
  // A picture reads at any size.
  image: ANY_SIZE,
};

/** How a pinned frame of `frameType` may be sized. */
export function widgetSizingFor(frameType: FrameType): WidgetSizing {
  const listed = SIZINGS[frameType];
  if (listed) return listed;
  const showsSmall =
    widgetFaceFor(frameType).Face !== null || WIDGET_FRAME_TYPES.has(frameType);
  return showsSmall ? ANY_SIZE : ROOMY;
}

/** The sizes the widget's size row offers: none without a choice. */
export function widgetSizesToPick(sizing: WidgetSizing): readonly WidgetSize[] {
  return sizing.sizes === "free" || sizing.sizes.length < 2 ? [] : sizing.sizes;
}

/**
 * The widget size a new pin starts at: `asked` (an agent's `desktop_pin`
 * size) when `sizing` allows it, at least the free minimum for a frame sized
 * by the hand; else the type's starting size.
 */
export function pinStartSize(
  sizing: WidgetSizing,
  asked?: WidgetSize | null,
): WidgetSize {
  if (!asked) return sizing.initial;
  const allowed =
    sizing.sizes === "free"
      ? WIDGET_SIZE_ORDER.indexOf(asked) >=
        WIDGET_SIZE_ORDER.indexOf(sizing.min)
      : sizing.sizes.includes(asked);
  return allowed ? asked : sizing.initial;
}
